import * as React from 'react';
import {Platform, View} from 'react-native';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Markdown} from '../../shared/components/markdown';
import {ResponsiveView} from '../../shared/components/responsiveView';
import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Text} from '../../shared/components/text';
import {useStyles} from '../../shared/hooks/styles';
import {Content} from '../../shared/models/content';
import {Routes, Screens} from '../../shared/routing';
import {useTheme} from '../../shared/theme';
import {DeviceSize, maxDesktopContentWidth, useDeviceSize} from '../../shared/theme/helpers';
import {Image} from '../../shared/components/image';
import {Paper, makeStyles} from '@material-ui/core';
import {CASHBACK_FORM_URL} from 'react-native-dotenv';
import {ContentWithRightImage} from '../../shared/components/contentWithRightImage';
import {CashbackPng} from '../../assets/images';
import WebView from 'react-native-webview';
import {LoadableContent} from '../../shared/components/loadableContent';

interface Props {
  content: Content;
}

export default ({content}: Props) => {
  const routes = [
    {
      title: 'Возможности карты',
      routeName: Screens.CardServices,
      webRoute: Routes.cardService,
    },
    {
      title: 'Регистрация в акциях Программы лояльности АО «НСПК»',
      routeName: Screens.Cashback,
      webRoute: Routes.cashBack,
    },
  ];
  const cashBackFormUrl =
    CASHBACK_FORM_URL || 'https://form.vamprivet.ru/register-iframe/resident_card/SVERDLOVSK_URALOCHKA_CARD/';
  const residentCardIFrame = React.useRef<HTMLIFrameElement>(null);
  const theme = useTheme();
  const deviceSize = useDeviceSize();
  const [isLoading, setIsLoading] = React.useState(true);

  const frameStyles = makeStyles((t) => ({
    cashBackProgramFrame: {
      padding: 0,
      border: 0,
      width: isLoading ? 0 : '100%',
      height: isLoading ? '0 !important' : '40rem',
      [t.breakpoints.down(DeviceSize.tablet)]: {
        height: isLoading ? '0 !important' : '40rem',
        width: isLoading ? '0 !important' : 'auto',
      },
      [t.breakpoints.down(695)]: {
        height: isLoading ? '0 !important' : '45rem',
        width: isLoading ? '0 !important' : '100%',
        border: 0,
      },
    },
    cashBackFrameContainer: {
      padding: 0,
      marginTop: '2rem',
      marginBottom: '6rem',
      borderRadius: 14,
      height: '100%',
      width: '40rem',
      [t.breakpoints.down(695)]: {
        width: '100%',
        height: 'auto',
        alignSelf: 'center',
        minHeight: '42rem',
      },
      [t.breakpoints.down(992)]: {
        alignSelf: 'center',
      },
      ...(Platform.OS === 'ios' && {alignSelf: 'center'}),
      overflow: 'hidden !important',
    },
    cashBackProgramAgreementBox: {
      margin: '1rem',
    },
  }))();

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const styles = useStyles(({mediaOnly, mediaExclude}) => {
    return {
      sectionContainer: {
        flexGrow: 1,
        flexDirection: 'column',
        alignItems: 'center',
        ...mediaOnly(DeviceSize.mobile, {paddingHorizontal: 16}, {}),
        ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 35}, {}),
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 60}, {}),
      },
      contentTextWrapper: mediaOnly(DeviceSize.desktop, {marginBottom: 0}, {marginBottom: 30}),
      contentContainer: {
        width: '100%',
        maxWidth: maxDesktopContentWidth,
        flexDirection: 'column',
        alignItems: 'stretch',
      },
      programSectionContainer: {
        ...mediaOnly(DeviceSize.desktop, {marginTop: 32}, {marginTop: 20}),
        backgroundColor: theme.screen.container[deviceSize].backgroundColor,
      },
      programTitle: {
        ...theme.fonts.h2,
        marginBottom: 30,
        marginTop: 40,
        width: '100%',
        ...mediaOnly(DeviceSize.mobile, {...theme.fonts.h5}),
      },
      programContentWithImageContainer: {},
      programContentWithImageIcon: {
        marginTop: -65,
        marginRight: 35,
        width: 397,
        height: 352,
      },
      programContentWithImageBackground: {
        ...mediaOnly(DeviceSize.desktop, {top: -57, right: 54, width: 424, height: 436}, {}),
      },
      programContentWithImageContent: mediaOnly(DeviceSize.mobile, {minHeight: 1}, {minHeight: 'auto'}),
      programDescription: mediaOnly(DeviceSize.desktop, {marginBottom: 40}, {marginBottom: 30}),
      programDescriptionText: mediaOnly(DeviceSize.desktop, theme.fonts.h6Light, theme.fonts.bodySmallLight),
      programFeaturesTitle: mediaOnly(DeviceSize.desktop, {marginBottom: 32}, {marginBottom: 20}),
      programFeaturesTitleText: mediaOnly(DeviceSize.desktop, theme.fonts.h3, theme.fonts.action),
      programFeaturesList: {
        ...mediaOnly(
          DeviceSize.desktop,
          {
            width: 600,
            maxWidth: '100%',
            marginBottom: 60,
          },
          {marginBottom: 20},
        ),
      },
      acceptorsSectionContainer: {
        backgroundColor: theme.color.whiteTextColor,
        ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 25}, {}),
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 50}, {}),
      },
      acceptorsContentContainer: {
        maxWidth: maxDesktopContentWidth + 20,
      },
      acceptorsTitle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
        ...mediaOnly(DeviceSize.desktop, {marginTop: 60, marginBottom: 40}, {marginTop: 30, marginBottom: 14}),
        ...mediaOnly(DeviceSize.mobile, {}, {paddingHorizontal: 10}),
      },
      acceptorsTitleText: {
        ...mediaOnly(DeviceSize.desktop, {...theme.fonts.h2}, {...theme.fonts.h6}),
        color: theme.color.textColor,
      },
      acceptorsButtonContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        ...mediaOnly(DeviceSize.mobile, {}, {paddingHorizontal: 10}),
      },
      acceptorsFilterButtonMobile: {
        width: '100%',
        marginTop: 10,
        marginBottom: 24,
        marginLeft: 0,
        marginRight: 0,
        ...mediaExclude(DeviceSize.mobile, {maxWidth: 328}),
      },
      webView: {
        borderRadius: 5,
        minHeight: 640,
      },
      cashBackProgramAgreementButtonBox: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      },
    };
  });

  return (
    <Screen fullWidth>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <View style={[styles.sectionContainer, styles.programSectionContainer]}>
          <View style={styles.contentContainer}>
            <Breadcrumbs routes={routes} />
            <Text testID='baby-food-title-id' style={styles.programTitle}>
              {`Регистрация в акциях\nПрограммы лояльности АО «НСПК»`}
            </Text>
            <ContentWithRightImage
              hideImage={deviceSize !== DeviceSize.desktop}
              containerStyle={styles.programContentWithImageContainer}
              icon={<Image source={CashbackPng as any} width={397} height={352} />}
              iconStyle={styles.programContentWithImageIcon}
              backgroundStyle={styles.programContentWithImageBackground}
              contentStyle={styles.programContentWithImageContent}>
              <View style={styles.contentTextWrapper}>
                <Markdown listColumns={deviceSize === DeviceSize.desktop ? 2 : 1}>
                  {content.services.cashBack.descriptionMarkdown}
                </Markdown>
                {Platform.OS === 'web' && (
                  <Paper elevation={6} className={frameStyles.cashBackFrameContainer}>
                    {isLoading && <LoadableContent loading={isLoading} />}
                    <iframe
                      title='NSPKframe'
                      ref={residentCardIFrame}
                      onLoad={handleIframeLoad}
                      className={frameStyles.cashBackProgramFrame}
                      src={cashBackFormUrl}
                    />
                  </Paper>
                )}
                {Platform.OS !== 'web' && (
                  <WebView
                    startInLoadingState
                    mixedContentMode='compatibility'
                    source={{uri: cashBackFormUrl}}
                    containerStyle={styles.webView}
                    scrollEnabled={false}
                    overScrollMode={'never'}
                  />
                )}
              </View>
            </ContentWithRightImage>
          </View>
        </View>
      </CustomScrollView>
    </Screen>
  );
};
